// What this session's processes did on the endpoint, read against a trace's
// turn windows.
//
// The scope is the SESSION, not the machine — see sessionScope in
// traceTurnClockFetch for why an OS event carrying the session's
// notification_id came from the agent's process tree by construction, and what
// that leaves out. Two things follow for this hook. The reading no longer mixes
// in a second agent session that happened to be running on the same box, which
// on the sampled staging traces was nearly half of what an endpoint-wide read
// returned. And the endpoint id is no longer the thing a read is keyed on, so
// this hook needs the session as well and will not read without it.
//
// ONE ambient query per trace, not one per window. The rungs of a turn clock
// tile a continuous interval — every window's floor is some prior request's
// capture and every ceiling is at or below the focal one — so the whole trace
// fits inside [first prior capture, focal capture], and joinEventsToClock
// already slices a flat event list into rungs.
//
// CAUTION — the measurement that argued for this no longer describes the query.
// `events` is ORDER BY (tenant_id, notification_id, event_id) and PARTITION BY
// (tenant_id, day). When the read was scoped to the ENDPOINT, endpoint_id was in
// neither, so every query scanned a whole day partition and a narrow window cost
// what a wide one did: a two-minute window over a busy endpoint read 258k rows
// to return 13. One span query instead of forty window queries was the obvious
// win, and that is why the window budget went.
//
// Moving the scope to the SESSION changed the storage argument, because
// notification_id IS the sort key's second column. Re-measured on staging with
// the predicate the code now sends:
//
//   one 2-minute window   1,446 rows read,  0.1 MB
//   the whole 12-day span 662,933 rows read, 36.6 MB — and TRUNCATED at
//                         SPAN_EVENT_LIMIT, so the tail of the trace reads UNREAD
//
// So for a long session the span read is now the expensive one and the thing it
// replaced is cheap. Reverting to per-window is a real design question and not a
// drive-by: it trades one round trip for dozens, and the truncation it would fix
// only bites sessions past the cap. Left as it is, deliberately, but do not cite
// the 258k figure to justify anything — it measured a query this file no longer
// issues.
//
// The detail leg stays per-window and stays budgeted, and that budget still
// earns its place on BYTES rather than rows: it projects event_data, so one
// window's read returns a handful of rows and pulls ~10 MB.
//
// NO DWELL OF ITS OWN, deliberately (traceReadDwell). The span read is the
// heaviest query the panel issues and it is also the last one to be reachable:
// `span` comes from the clock, which does not fetch until the reader has
// settled, and a window cannot be requested until a probe has dated the rung
// that bounds it. So the ~1.4M-row scan already goes out a full dwell plus two
// round trips after the trace was opened — a second timer here would re-charge
// the reader for time they have demonstrably already spent, and bound nothing
// the first one does not.
//
// Failure is deliberately loud in the state and quiet in the UI, and the two
// must not be confused: a lookup that failed means "no corroboration available",
// never "this session's processes did nothing". Those read identically if a
// failure is allowed to fall through to an empty list, and the second is a claim
// about the session that nobody made. The same holds for the far edge of a
// truncated span — a window past the last event read is UNREAD, and a busy
// afternoon that renders as a quiet one is the exact lie this module exists to
// prevent.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { joinEventsToClock } from "./traceTurnClock.js";
import { fetchEndpointEvents, fetchEndpointEventDetails } from "./traceTurnClockFetch.js";
import { MATCHABLE_EVENT_TYPES } from "./traceEndpointMatch.js";
import { COMMAND_EVENT_TYPES } from "./apiClient.js";

// The ceiling on one trace's overview. Typed reads page metadata within the
// planner's 1,000-row query limit while retaining the existing coverage bound.
//
// It binds on busy endpoints and is meant to: sampled over long staging traces,
// the endpoint produced ~2,000 matching events inside the trace's span at p50
// and ~53,000 at p90. Under the cap the whole trace is read. Over it, the tail
// past the last returned event reads as UNREAD, which is the honest answer and
// the only one that keeps a busy stretch from rendering as a quiet one.
export const SPAN_EVENT_LIMIT = 20000;
// The detail leg carries typed family fields, so it is budgeted separately:
// far fewer windows can want it — those whose rows name a path or ran a command,
// plus the ones a reader actually opens. The overview needs only timestamps
// and event identities.
export const MAX_DETAIL_WINDOWS = 12;
const DETAIL_LIMIT = 200;

// The (startTs, endTs] rule lives in joinEventsToClock and nowhere else: two
// places disagreeing about which side of a capture an event falls on is a
// difference nobody would ever see.
function joinToWindow(win, events) {
  const rung = { fromTurn: 0, toTurn: 0, startTs: win.startTs, endTs: win.endTs };
  return joinEventsToClock([rung], events).get(0) || [];
}

/**
 * What the span read says about ONE window.
 *
 * `coverageTo` is the instant the reading provably reached, and it only means
 * anything when the read was `truncated` — an untruncated read covers the whole
 * span, and comparing against its far edge would mark the trace's last window
 * short for touching the boundary it is supposed to touch.
 *
 * Three cases, and the middle one is worth naming: a window the reading entered
 * and did not finish holds real events AND is short, so it says both rather than
 * picking one. Its bound is `>=` and not `>` on purpose — the cut fell at
 * coverageTo, so a window ending exactly there may have lost events at that very
 * instant, and claiming completeness on the boundary is the one place this could
 * under-report without saying so.
 */
function windowFromSpan(win, span) {
  if (span.failed) return { status: "unavailable", reason: "failed" };
  if (span.truncated && win.startTs >= span.coverageTo) return { status: "unavailable", reason: "truncated" };
  return {
    status: "ready",
    events: joinToWindow(win, span.events),
    capped: span.truncated && win.endTs >= span.coverageTo,
    detail: null,
  };
}

/**
 * @param sessionId   the trace's session; the scope every read is keyed on, and
 *                    without it there is nothing to join
 * @param endpointId  the trace's endpoint. Redundant against the session key,
 *                    which names one machine by construction, and kept so a read
 *                    cannot answer about a machine the caller did not name
 * @param span        { fromTs, toTs } covering every window this trace can
 *                    produce — useTurnClock's `span`. Null until the priors
 *                    land, and null forever for a session with no priors, which
 *                    has no two-sided rung to corroborate anyway.
 * @param enabled     false skips all work
 *
 * @returns { request(window, wantDetail, interactive), resultFor(key), coverage, busy, inFlight }
 *   `interactive` marks an ask the reader made by hand. Such an ask is exempt
 *   from the detail budget and can retry a window the budget refused; a
 *   speculative ask cannot.
 *   An entry is `{ status: "ready", events, capped, detail }` or
 *   `{ status: "unavailable", reason: "failed" | "truncated" }`. A window never
 *   asked about has no entry at all, which is how an un-fetched window costs
 *   nothing to render.
 *
 *   `coverage` is `{ read, unread, failed }` over the windows asked about so
 *   far — how far the reading actually got, which no single window can say.
 *   Every mark on a rail is one window's answer, so a trace whose span was cut
 *   renders the same refusal dozens of times and each one reads as a fact about
 *   the boundary it sits on. The counts are the fact about the READING.
 *
 *   `detail` is the name-matchable projection, and its shape is the reading:
 *   `null` — never asked, because nothing wanted names for this window;
 *   `{ events, capped }` — read, `capped` meaning names were only read that far;
 *   `{ failed: true }` / `{ budget: true }` — asked and not answered, which is
 *   NOT the same as a window with no correspondence in it.
 */
export function useEndpointCorroboration({ sessionId, endpointId, span = null, enabled = true }) {
  const [entries, setEntries] = useState(() => new Map());
  const [inFlight, setInFlight] = useState(0);
  // Every window asked about, whether or not the span has landed. The span read
  // answers ALL of them at once, so the ones asked before it lands have to be
  // remembered or they would sit entryless until something re-asked.
  const askedRef = useRef(new Map());
  // The one ambient answer, shared by every window: `{ events, coverageTo }` or
  // `{ failed: true }`.
  const spanResultRef = useRef(null);
  const spanStartedRef = useRef(false);
  // Windows the detail leg actually went out for. This is what the budget
  // counts, so a window it refused is NOT in here — it is in detailRefusedRef,
  // which exists so a later open can tell "the budget said no" apart from "never
  // asked" and retry the first.
  const detailStartedRef = useRef(new Set());
  const detailRefusedRef = useRef(new Set());
  // Windows the span read cannot make `ready` — it failed, or they sit past its
  // far edge. Held because the detail leg has to honour that: only the ambient
  // reading makes an entry `ready` and applyDetail patches nothing else, so a
  // name read for one of these has nowhere to land.
  const neverReadyRef = useRef(new Set());
  // Detail landing BEFORE the span is a real ordering — two queries, two round
  // trips. Held here so whichever leg is second composes the pair instead of
  // overwriting it.
  const detailsRef = useRef(new Map());
  // Which trace a fetch belongs to. A late result landing after the panel moved
  // to another trace would otherwise write one machine's activity into another's
  // windows — and the windows themselves can genuinely repeat, since two
  // requests of one session share the same ladder.
  const epochRef = useRef(0);
  // The epoch guard only discards the answer; the scan has already run. On a
  // rail of dozens of windows that is a whole trace's worth of ClickHouse work
  // left behind by one click.
  const abortRef = useRef(null);
  // The interactive name read, separately abortable. Exactly one window is open
  // at a time (TraceWorkFeed holds a single `openWindow` key), so a second
  // interactive read means the reader has moved off the first and its answer has
  // nowhere to land. Superseding holds a walk down a long trace at ONE live name
  // query rather than one per window visited — worth it for the PAYLOAD, which
  // is what the detail leg is budgeted for: measured on staging, one window's
  // read returns three rows and pulls 10.4 MB, because `event_data` is the
  // heavy column. Its row scan is small (~1.5k), so this bounds bytes and
  // connections, not a partition scan.
  //
  // KNOWN LIMIT, and deliberately left: the abort ends the browser's wait and
  // nothing further. Nothing between here and ClickHouse propagates a client
  // disconnect — restClient's fetch, grpc-gateway, api-server's execute_query,
  // shared-analytics::execute — and the CH client sets `wait_end_of_query=1`,
  // which buffers the result server-side so a disconnect goes unnoticed until
  // the write. A superseded query therefore runs to completion.
  //
  // Making it not do so means enabling `cancel_http_readonly_queries_on_client_close`
  // (set nowhere in the repo today) and revisiting `wait_end_of_query` on the
  // execution path EVERY analytics query in the product shares. That is not a
  // trade this read can justify: measured, it is 1,458 rows and 0.03s. If
  // cancellation is ever worth building, the case for it is the span read above
  // — 662,933 rows, and it truncates — not this one.
  const interactiveAbortRef = useRef(null);

  const idKey = `${sessionId || ""}:${endpointId || ""}:${span ? `${span.fromTs}-${span.toTs}` : ""}:${enabled ? 1 : 0}`;
  const seenRef = useRef(idKey);
  useEffect(() => {
    // Skipped on the mount pass. Child effects run before this one, so a window
    // that requested itself during the same commit would have its epoch bumped
    // out from under it and never be retried.
    if (seenRef.current === idKey) return;
    seenRef.current = idKey;
    // Nothing has been spent under the previous key, so there is nothing to
    // discard — and bumping the epoch anyway would be actively wrong. The span
    // arrives a commit AFTER the hook mounts (the clock publishes it with
    // `ready`), child effects run before this one, and a window that requested
    // itself in that commit would have its read cancelled out from under it.
    if (!spanStartedRef.current) return;
    askedRef.current = new Map();
    spanResultRef.current = null;
    spanStartedRef.current = false;
    detailStartedRef.current = new Set();
    detailRefusedRef.current = new Set();
    neverReadyRef.current = new Set();
    detailsRef.current = new Map();
    epochRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    interactiveAbortRef.current?.abort();
    interactiveAbortRef.current = null;
    setEntries(new Map());
    setInFlight(0);
  }, [idKey]);

  // Unmount is not an endpoint change and gets no epoch bump, so it needs its
  // own withdrawal — closing the panel mid-read is the same abandoned scan.
  useEffect(() => () => {
    abortRef.current?.abort();
    interactiveAbortRef.current?.abort();
  }, []);

  const signalFor = useCallback(() => {
    if (!abortRef.current) abortRef.current = new AbortController();
    return abortRef.current.signal;
  }, []);

  // One answer serves every window asked about, including the ones asked while
  // it was in flight — which is most of them, since the feed dates rows as they
  // scroll and the span goes out on the first one.
  const resolveAsked = useCallback(() => {
    const result = spanResultRef.current;
    setEntries((prev) => {
      const next = new Map(prev);
      for (const [key, win] of askedRef.current) {
        const entry = windowFromSpan(win, result);
        if (entry.status !== "ready") {
          neverReadyRef.current.add(key);
          // A name query dispatched alongside the span is already on the wire
          // and its answer can now never land, so the slot it took goes back
          // rather than staying spent on a window that can never show a match.
          // Safe only because neverReadyRef turns this window away at the top of
          // every later ask: a slot the same window could re-spend on its next
          // boundary render would be a loop, not a refund.
          detailStartedRef.current.delete(key);
        } else {
          entry.detail = detailsRef.current.get(key) ?? null;
        }
        next.set(key, entry);
      }
      return next;
    });
  }, []);

  const request = useCallback(
    (win, wantDetail = false, interactive = false) => {
      if (!enabled || !sessionId || !win || !span) return;
      const epoch = epochRef.current;
      const signal = signalFor();
      // An abort is this hook's own doing, not a fault: on an endpoint change
      // the epoch guard already discards it, but on UNMOUNT there is no epoch
      // bump, so the withdrawal has to be recognised here or a closed panel logs
      // a failed lookup and writes to state nothing is reading.
      const withdrawn = () => signal.aborted || epoch !== epochRef.current;
      const settle = () => {
        if (epoch === epochRef.current) setInFlight((n) => Math.max(0, n - 1));
      };
      const applyDetail = (detail) => {
        detailsRef.current.set(win.key, detail);
        setEntries((prev) => {
          const cur = prev.get(win.key);
          // The ambient reading has not landed yet; it will read detailsRef when
          // it does. Patching a missing entry into existence here would invent a
          // ready window out of half its evidence.
          if (!cur || cur.status !== "ready") return prev;
          return new Map(prev).set(win.key, { ...cur, detail });
        });
      };

      if (!askedRef.current.has(win.key)) {
        askedRef.current.set(win.key, win);
        const result = spanResultRef.current;
        if (result) {
          // The span is already in hand: this window's answer costs a slice of
          // an array, which is the whole point of reading the trace once.
          const entry = windowFromSpan(win, result);
          if (entry.status !== "ready") neverReadyRef.current.add(win.key);
          else entry.detail = detailsRef.current.get(win.key) ?? null;
          setEntries((prev) => new Map(prev).set(win.key, entry));
        }
      }

      if (!spanStartedRef.current) {
        spanStartedRef.current = true;
        setInFlight((n) => n + 1);
        fetchEndpointEvents({
          sessionId,
          endpointId,
          fromTs: span.fromTs,
          toTs: span.toTs,
          eventTypes: COMMAND_EVENT_TYPES,
          limit: SPAN_EVENT_LIMIT,
          signal,
        }).then(
          (events) => {
            if (withdrawn()) return;
            settle();
            // A short result read the whole span; a full one stopped at its last
            // event, and everything after that instant is unread rather than
            // empty. Taking span.toTs either way is the lie: it would report a
            // busy afternoon as a quiet machine.
            const truncated = events.length >= SPAN_EVENT_LIMIT;
            const coverageTo = truncated ? events[events.length - 1].ts : span.toTs;
            spanResultRef.current = { events, coverageTo, truncated, failed: false };
            resolveAsked();
          },
          (err) => {
            if (withdrawn()) return;
            settle();
            // Quiet in the UI, not in the console — the same bargain
            // useTurnClock strikes, and for the same reason: an empty reading
            // and a failed one look alike from the page.
            console.warn("[endpoint-corroboration] endpoint lookup failed; no corroboration for this trace", err);
            spanResultRef.current = { events: [], coverageTo: span.fromTs, truncated: false, failed: true };
            resolveAsked();
          },
        );
      }

      // Nothing more is spent on a window that can no longer become `ready`.
      // Every boundary re-asks as the feed re-renders, so without this those
      // windows would take the harder budget's slots away from the ones that can
      // still use them, and pay for name lookups whose answers can never show.
      if (neverReadyRef.current.has(win.key)) return;
      // On a window's first ask with names wanted this leg goes out before the
      // span has answered, and that overlap is deliberate: names are wanted from
      // the first render wherever the rows already name a path, so an opened
      // window shows what ran as soon as it can rather than a second round trip
      // after the counts land.
      if (!wantDetail || detailStartedRef.current.has(win.key)) return;
      // The budget bounds SPECULATION. Every window whose rows name a path wants
      // names before anyone asks for them, which in a working trace is most of
      // them, so the cap is spent in mount order — an order that reflects how the
      // reader scrolled and nothing about the windows. An open is the reader
      // naming one window by hand, so it is never refused, and it retries one the
      // prefetch already turned away. A click losing to a fetch nobody asked for,
      // with no way to try again, is the worst refusal this could make.
      if (!interactive) {
        if (detailRefusedRef.current.has(win.key)) return;
        if (detailStartedRef.current.size >= MAX_DETAIL_WINDOWS) {
          detailRefusedRef.current.add(win.key);
          applyDetail({ budget: true });
          return;
        }
      }
      detailStartedRef.current.add(win.key);
      // Back to "no reading yet" so the window says it is working. The loader is
      // keyed on the ABSENCE of a detail, so a retry still holding its refusal
      // would sit on "names not read" until the answer landed.
      if (detailRefusedRef.current.delete(win.key)) applyDetail(null);
      // Only the interactive leg is superseded. A speculative read is already
      // capped and its windows are the ones still on screen, so cancelling those
      // would buy nothing and lose answers the reader is about to scroll to.
      let detailSignal = signal;
      if (interactive) {
        interactiveAbortRef.current?.abort();
        interactiveAbortRef.current = new AbortController();
        detailSignal = interactiveAbortRef.current.signal;
      }
      const detailWithdrawn = () => detailSignal.aborted || withdrawn();
      setInFlight((n) => n + 1);
      // Never taken as a failure of the pair: the detail leg is an addition to
      // the ambient reading and must not take it down with it. A window whose
      // names could not be read says so; it does not report itself as a window
      // with no correspondence.
      fetchEndpointEventDetails({
        sessionId,
        endpointId,
        fromTs: win.startTs,
        toTs: win.endTs,
        eventTypes: MATCHABLE_EVENT_TYPES,
        limit: DETAIL_LIMIT,
        signal: detailSignal,
      }).then(
        (rows) => {
          if (detailWithdrawn()) return;
          settle();
          applyDetail({ events: joinToWindow(win, rows), capped: rows.length >= DETAIL_LIMIT });
        },
        (err) => {
          if (detailWithdrawn()) {
            // Superseded rather than torn down: the reader opened another
            // window. Hand the slot back and mark the window refused, so a
            // re-open re-reads it and the feed's re-ask on every boundary
            // render does not (the same refund/loop bargain as a failed span).
            if (!withdrawn()) {
              settle();
              detailStartedRef.current.delete(win.key);
              detailRefusedRef.current.add(win.key);
            }
            return;
          }
          settle();
          console.warn("[endpoint-corroboration] name lookup failed; ambient counts only for this window", err);
          applyDetail({ failed: true });
        },
      );
    },
    [enabled, sessionId, endpointId, span, signalFor, resolveAsked],
  );

  const resultFor = useCallback((key) => entries.get(key) || null, [entries]);

  // Counted over the entries rather than the refs so it is the same reading the
  // page shows: a window still in flight has no entry and is scored as neither
  // read nor unread, which is what "so far" means while a reader is scrolling.
  const coverage = useMemo(() => {
    let read = 0;
    let unread = 0;
    let failed = 0;
    for (const entry of entries.values()) {
      if (entry.status === "ready") read += 1;
      else if (entry.reason === "truncated") unread += 1;
      else failed += 1;
    }
    return { read, unread, failed };
  }, [entries]);

  return useMemo(
    () => ({ request, resultFor, coverage, busy: inFlight > 0, inFlight }),
    [request, resultFor, coverage, inFlight],
  );
}
